import { BackendStatus } from './backendstatus.interface';
import { PlayerLogService } from './PlayerLog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Observer } from 'rxjs/Observer';
import 'rxjs/add/operator/timeout';
import { Subject } from 'rxjs/Subject';
import { Injectable, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { $WebSocket, WebSocketSendMode } from 'angular2-websocket/angular2-websocket';
import { UUID } from 'angular2-uuid';


@Injectable()
export class PlayerManagementService {

  public ws: $WebSocket;
  public received$: Subject<MessageEvent>;
  public connected: boolean;
  public managementHost: string;
  public isPlayerManaged = false;
  public managedHostChanged = new EventEmitter();
  public connectionStatusChanged = new EventEmitter<boolean>();
  public sentBrowserReady = false;
  private socket;

  constructor(
    public activatedRoute: ActivatedRoute,
    public router: Router
  ) {

    this.activatedRoute.queryParams.take(1).subscribe(async (value) => {

      // save the management address and state on initial load
      if (value.managedhost) {
        this.managementHost = value.managedhost;
        this.isPlayerManaged = true;

        // try to connect
        if (!this.connected) {
          this.wsConnect();
        }
      }

    });

  }

  wsConnect() {
    this.connect(`ws://localhost:5000`);
  }

  public async postMessage(message) {
    if (!this.ws || !this.connected) {
      return;
    }
    try {
      await this.ws.send4Promise(JSON.stringify(message));
      message = null; // gc
    } catch (error) {
      console.log(error.message);
    }

  }

  private async postMessageWithResponse(message, timeout?): Promise<any> {
    const msgKey = UUID.UUID(); // create unique transaction key
    message.key = msgKey; // add key to message

    if (!timeout) {
      timeout = 5000; // 5 second default timeout
    }

    // start listing for all messages, stop when correct key received, or timeout
    const newPromise = new Promise((resolve, reject) => {
      const responseSub$ = this.received$.timeout(5000).subscribe(responseMessage => {
        if (responseMessage && responseMessage.data) {
          const response = JSON.parse(responseMessage.data);
          // only complete if transaction key matches
          if (response && response.key === msgKey) {
            delete response.key; // delete temp transaction key
            responseSub$.unsubscribe();
            return resolve(response);
          }
        }
        resolve();
      }, error => {
        responseSub$.unsubscribe();
        // this.logger.error(`Did not receive response to '${message.command}' command`);
        reject();
      });

    });

    // send message with transaction key
    this.postMessage(message);
    return newPromise;
  }

  public connect(url): Subject<MessageEvent> {

    if (!this.received$) {
      this.received$ = this.create(url);
      this.connected = true;
      console.log('Successfully connected: ' + url);
      this.connectionStatusChanged.emit(true);

    } else {
      console.log('Could not connect to local ws server: ' + url);
      this.connected = false;
      this.connectionStatusChanged.emit(false);
    }
    return this.received$;
  }

  public disconnect() {
    if (this.ws && this.connected) {
      this.ws.close();
      this.connected = false;
      this.connectionStatusChanged.emit(false);
    }
  }

  private create(url): Subject<MessageEvent> {
    this.ws = new $WebSocket(url);

    this.received$ = this.ws.getDataStream();

    return this.received$;
  }

  //#region Backend Commands
  async sendBrowserReady() {

    // 500ms delay.. give slow computers time to settle
    await new Promise(resolve => setTimeout(resolve, 500));

    this.postMessage('Browser Ready');
    if (!this.sentBrowserReady) {
      this.sentBrowserReady = true;
    }

  }

  async getStatus(): Promise<BackendStatus> {

    // send scheduledata to backend
    if (this.isPlayerManaged) {
      const message = {
        command: 'getStatus',
        value: {},
        source: 'pms.getStatus()',
        timestamp: new Date(),
      };

      const backendStatus = await this.postMessageWithResponse(message);
      return backendStatus;
    }

    return;
  }

  async updateSchedule(): Promise<boolean> {

    // send scheduledata to backend
    if (this.isPlayerManaged) {
      const message = {
        command: 'updateSchedule',
        value: {},
        source: 'pms.updateSchedule()',
        timestamp: new Date(),
      };

      const result = await this.postMessageWithResponse(message);
      return true;
    }

    return;
  }

  async clearCache(): Promise<boolean> {

    // send scheduledata to backend
    if (this.isPlayerManaged) {
      const message = {
        command: 'clearCache',
        value: {},
        source: 'pms.clearCache()',
        timestamp: new Date(),
      };

      const result = await this.postMessageWithResponse(message);
      return true;
    }

    return;
  }

  async sendHeartbeat(): Promise<boolean> {

    // send scheduledata to backend
    if (this.isPlayerManaged) {
      const message = {
        command: 'sendHeartbeat',
        value: {},
        source: 'pms.sendHeartbeat()',
        timestamp: new Date(),
      };

      const result = await this.postMessageWithResponse(message);
      return true;
    }

    return;
  }

  async restart(mode: string): Promise<boolean> {
    // mode can be browser, process, or computer

    // send scheduledata to backend
    if (this.isPlayerManaged) {
      const message = {
        command: 'restart',
        value: mode,
        source: 'pms.restart()',
        timestamp: new Date(),
      };

      const result = await this.postMessageWithResponse(message);
      return true;
    }

    return;
  }

  async setPlayer(player) {

    if (this.isPlayerManaged) {
      const message = {
        command: 'setPlayer',
        value: {
          player: player,
        },
        source: 'pms.setPlayer',
        timestamp: new Date(),
      };

      this.postMessage(message);
    }
  }


  async sendScheduleData(scheduleData) {

    if (this.isPlayerManaged) {
      const message = {
        command: 'realtimeScheduleUpdate',
        value: {
          scheduleData: scheduleData,
        },
        source: 'pms.sendScheduleData',
        timestamp: new Date(),
      };

      this.postMessage(message);
    }
  }

  async pong() {
    this.postMessage({
      command: 'pong',
      value: {
        time: Date.now()
      }
    });
  }

  async setSerial(serial, hash, token, player) {
    if (this.isPlayerManaged) {
      const message = {
        command: 'serialSet',
        value: {
          serial: serial,
          hwhash: hash,
          isActivated: true, // set global activation flag
          player: player,
          token: token.id,
          playerId: player.id,
          ttl: token.ttl
        }
      };
      return await this.postMessageWithResponse(message);
    }
  }

  async sendLog(targetLog, level, data) {
    if (this.connected) {
      await this.postMessage({
        log:
        {
          targetLog: targetLog,
          level: level,
          message: data
        }
      });
    }
  }

  async changeHost(data): Promise<boolean> {

    // send scheduledata to backend
    if (this.isPlayerManaged) {
      const message = {
        command: 'changeHost',
        value: data,
        source: 'pms.changeHost()',
        timestamp: new Date(),
      };

      const result = await this.postMessageWithResponse(message);
      return true;
    }

    return;
  }

  async changeSerial(data): Promise<boolean> {

    // send scheduledata to backend
    if (this.isPlayerManaged) {
      const message = {
        command: 'changeSerial',
        value: data,
        source: 'pms.changeSerial()',
        timestamp: new Date(),
      };

      const result = await this.postMessageWithResponse(message);
      return true;
    }

    return;
  }

  async sendPlayStatus(currentlyPlayingRotationItemId: string, nextPlayRotationItemId: string, preloadedScreenRotationItemId: string, isPlaying: boolean, currentlyPlayingParentId: string, nextPlayParentId: string, preloadScreenParentId: string) {

    if (this.isPlayerManaged) {
      const message = {
        command: 'sendPlayStatus',
        value: {
          currentlyPlayingRotationItemId: currentlyPlayingRotationItemId,
          nextPlayRotationItemId: nextPlayRotationItemId,
          preloadedScreenRotationItemId: preloadedScreenRotationItemId,
          currentlyPlayingParentId: currentlyPlayingParentId,
          nextPlayParentId: nextPlayParentId,
          preloadScreenParentId: preloadScreenParentId,
          isPlaying: isPlaying
        },
        source: 'pms.sendPlayStatus',
        timestamp: new Date(),
      };

      this.postMessage(message);
    }
  }

  //#endregion Backend Commands

}




// WEBPACK FOOTER //
// ./src/app/player/services/playermanagement.service.ts